import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslation } from 'react-i18next';
import { Briefcase, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { SERVICE_CATEGORIES } from '@/config/serviceCategories';

export default function Services() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isProvider, setIsProvider] = useState(false);

  useEffect(() => {
    const checkProviderStatus = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('service_providers')
        .select('id')
        .eq('user_id', user.id)
        .single();

      setIsProvider(!!data);
    };

    checkProviderStatus();
  }, [user]);

  const handleSearch = (type: string, emergency: boolean = false) => {
    navigate(`/services/search?type=${type}&emergency=${emergency}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      <div className="bg-card border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-3 mb-2">
            <Briefcase className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-center text-foreground">
              {t('services.title')}
            </h1>
          </div>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>
      </div>

      {isProvider && (
        <div className="container mx-auto px-4 pt-6">
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-blue-900 dark:text-blue-100">
                {t('services.providerAlert')}
              </span>
              <Button 
                size="sm" 
                onClick={() => navigate('/provider-dashboard')}
                className="ml-4"
              >
                {t('services.goToDashboard')}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICE_CATEGORIES.map((category) => (
            <Card 
              key={category.id}
              className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer bg-card"
              onClick={() => handleSearch(category.type, category.emergency)}
            >
              <div className={`h-32 bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                <span className="text-6xl">{category.icon}</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 text-foreground">
                  {t(category.titleKey)}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {t(category.descKey)}
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSearch(category.type, category.emergency);
                  }}
                >
                  {t('services.searchButton')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 pb-6">
        <Card className="bg-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1">
                {t('services.browseAllServices')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('services.browseAllServicesDescription')}
              </p>
            </div>
            <Button 
              onClick={() => navigate('/services/search?type=')}
              variant="outline"
            >
              {t('services.viewAll')}
            </Button>
          </div>
        </Card>
      </div>

      <div className="container mx-auto px-4 pb-12">
        <Card className="bg-gradient-to-r from-pet-purple to-pet-blue text-white p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            {t('services.registerProvider.banner.title')}
          </h2>
          <p className="mb-6 text-white/90">
            {t('services.registerProvider.banner.description')}
          </p>
          <Button 
            size="lg"
            variant="secondary"
            onClick={() => navigate('/register-provider')}
          >
            {t('services.registerProvider.banner.button')}
          </Button>
        </Card>
      </div>
    </div>
  );
}
